import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { sorted } from "helpers";
import {
  MixMetadata,
  MixSummary,
  PeriodSummary,
  PlannedResultsRead,
} from "src/store/api/generatedApi";
import {
  combineMixMaterials,
  withMaterials,
} from "components/plan/table/production/productionContent";
import { INVENTORY_COLUMN_WIDTH_PX } from "components/plan/table/production/inventoryColumns";

dayjs.extend(utc);

export const MAX_ARTEFACT_PERIODS = 6;

const SCRAP_COLUMN_WIDTH_PX = 200;
const MIX_COLUMN_WIDTH_PX = 150;
const CONTENT_PADDING_PX = 18;
const MIN_CONTENT_WIDTH_PX = 480;

export interface RecipeCompositionMaterial {
  name: string;
  mass: number;
  fraction: number;
}

export interface RecipeCompositionInventory {
  name: string;
  initial: number;
  final: number;
}

export interface RecipeCompositionSteelGrade {
  name: string;
  num_heats: number;
}

export interface RecipeCompositionMix {
  name: string;
  customer_mix_id: string | null;
  num_heats: number;
  steel_grades: RecipeCompositionSteelGrade[];
  total_mass_per_heat: number;
  cost_per_tonne: number;
  materials: RecipeCompositionMaterial[];
}

export interface RecipeCompositionPeriod {
  period: number;
  name: string | null;
  date: string;
  header: string;
  is_deployable: boolean;
  num_heats: number;
  has_mixes: boolean;
  mixes: RecipeCompositionMix[];
  inventory: RecipeCompositionInventory[];
}

export interface RecipeCompositionArtefactData {
  title: string;
  plan_id: number;
  has_content: boolean;
  max_mix_columns: number;
  content_width_px: number;
  periods: RecipeCompositionPeriod[];
}

export interface ArtefactPeriodInput {
  summary: PeriodSummary;
  date: string;
  header: string;
  results: PlannedResultsRead | null;
}

const roundTo = (value: number, decimalPlaces: number): number => {
  const factor = 10 ** decimalPlaces;
  return Math.round(value * factor) / factor;
};

export const parsePlanId = (raw: string | null): number | null => {
  if (raw === null) {
    return null;
  }
  const value = Number(raw);
  return Number.isInteger(value) && value > 0 ? value : null;
};

export const parsePeriods = (raw: string | null): number[] | null => {
  if (raw === null) {
    return null;
  }
  const parts = raw.split(",").map((part) => part.trim());
  if (parts.some((part) => part === "")) {
    return null;
  }
  const values = parts.map(Number);
  if (values.some((value) => !Number.isInteger(value) || value < 1)) {
    return null;
  }
  const unique = sorted([...new Set(values)], (value) => value);
  if (unique.length === 0 || unique.length > MAX_ARTEFACT_PERIODS) {
    return null;
  }
  return unique;
};

const ISO_DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

export const periodLabel = (summary: PeriodSummary, format: string): string => {
  if (summary.name !== null) {
    return ISO_DATE_PATTERN.test(summary.name)
      ? dayjs.utc(summary.name).format(format)
      : summary.name;
  }
  const isoDate = summary.start_timestamp?.split("T")[0] ?? null;
  if (isoDate !== null) {
    return dayjs.utc(isoDate).format(format);
  }
  return `Period ${summary.period}`;
};

export const buildTitle = (dates: string[]): string => {
  const last = dates[dates.length - 1] ?? "";
  const joined =
    dates.length <= 1
      ? (dates[0] ?? "")
      : `${dates.slice(0, -1).join(", ")} & ${last}`;
  return `Recipe: ${joined}`;
};

const inventoryWidthPx = (hasInventoryColumns: boolean): number =>
  hasInventoryColumns ? 2 * INVENTORY_COLUMN_WIDTH_PX : 0;

// True once any period carries the material summaries the table draws its
// inventory columns from, so the screenshot viewport and the table width agree
// with what the table actually renders.
export const hasInventory = (periods: RecipeCompositionPeriod[]): boolean =>
  periods.some((period) => period.inventory.length > 0);

export const contentWidthPx = (
  maxMixColumns: number,
  hasInventoryColumns: boolean
): number =>
  Math.max(
    MIN_CONTENT_WIDTH_PX,
    SCRAP_COLUMN_WIDTH_PX +
      MIX_COLUMN_WIDTH_PX * maxMixColumns +
      inventoryWidthPx(hasInventoryColumns) +
      CONTENT_PADDING_PX
  );

// Every period's table shares the widest table's width; a period with fewer
// mixes stretches its mix columns (never the scrap or inventory columns) to
// fill it.
export const tableWidthPx = (
  maxMixColumns: number,
  hasInventoryColumns: boolean
): number =>
  SCRAP_COLUMN_WIDTH_PX +
  MIX_COLUMN_WIDTH_PX * maxMixColumns +
  inventoryWidthPx(hasInventoryColumns) +
  2;

export const mixColumnWidthPx = (
  periodMixColumns: number,
  maxMixColumns: number
): number =>
  (MIX_COLUMN_WIDTH_PX * maxMixColumns) / Math.max(periodMixColumns, 1);

export const buildRawData = ({
  planId,
  title,
  periods,
  mixSummaries,
  mixMetadata,
}: {
  planId: number;
  title: string;
  periods: ArtefactPeriodInput[];
  mixSummaries: MixSummary[];
  mixMetadata: MixMetadata[];
}): RecipeCompositionArtefactData => {
  const periodData: RecipeCompositionPeriod[] = periods.map(
    ({ summary, date, header, results }) => {
      const periodResults = results?.periods[summary.period.toString()] ?? null;
      const resultMixes = periodResults
        ? withMaterials(periodResults.mixes)
        : [];

      const periodSummaries = mixSummaries.filter(
        (mixSummary) => mixSummary.period === summary.period
      );
      const chefGroupIdByMixId = Object.fromEntries(
        periodSummaries.map((mixSummary) => [
          mixSummary.mix_id,
          mixSummary.chef_group_id,
        ])
      );
      const mixNumberByMixId = Object.fromEntries(
        periodSummaries.map((mixSummary) => [
          mixSummary.mix_id,
          mixSummary.mix_number,
        ])
      );
      const customerMixIdByMixId = Object.fromEntries(
        mixMetadata
          .filter((metadata) => metadata.period === summary.period)
          .map((metadata) => [metadata.mix_id, metadata.customer_mix_id])
      );

      const mixes: RecipeCompositionMix[] = sorted(resultMixes, (result) => [
        chefGroupIdByMixId[result.mix.id] ?? 0,
        mixNumberByMixId[result.mix.id] ?? 0,
      ]).map((result) => {
        const materials = combineMixMaterials(result.mix).filter(
          (material) => material.mass > 0
        );
        const totalMass = materials.reduce(
          (sum, material) => sum + material.mass,
          0
        );
        return {
          name: result.name,
          customer_mix_id: customerMixIdByMixId[result.mix.id] ?? null,
          num_heats: result.num_heats,
          steel_grades: result.production.map((item) => ({
            name: item.steel_grade_name,
            num_heats: item.num_heats,
          })),
          total_mass_per_heat: roundTo(totalMass, 2),
          cost_per_tonne: roundTo(
            result.mix.summary?.cost.display_cost_per_tonne ?? 0,
            2
          ),
          materials: materials.map((material) => ({
            name: material.material_name,
            mass: roundTo(material.mass, 2),
            fraction: roundTo(totalMass > 0 ? material.mass / totalMass : 0, 4),
          })),
        };
      });

      const usedMaterialNames = new Set(
        mixes.flatMap((mix) => mix.materials.map((material) => material.name))
      );
      const inventory: RecipeCompositionInventory[] = (
        results?.summary.period_material_summary ?? []
      )
        .filter(
          (materialSummary) =>
            materialSummary.period === summary.period &&
            usedMaterialNames.has(materialSummary.material)
        )
        .map((materialSummary) => ({
          name: materialSummary.material,
          initial: roundTo(materialSummary.initial_inventory, 2),
          final: roundTo(materialSummary.final_inventory, 2),
        }));

      return {
        period: summary.period,
        name: summary.name,
        date,
        header,
        is_deployable: summary.is_deployable,
        num_heats: summary.num_heats,
        has_mixes: mixes.length > 0,
        mixes,
        inventory,
      };
    }
  );

  const maxMixColumns = Math.max(
    0,
    ...periodData.map((period) => period.mixes.length)
  );

  return {
    title,
    plan_id: planId,
    has_content: periodData.some((period) => period.has_mixes),
    max_mix_columns: maxMixColumns,
    content_width_px: contentWidthPx(maxMixColumns, hasInventory(periodData)),
    periods: periodData,
  };
};
